import React, { useState } from 'react';
import { useFeatureFlagContext } from 'lib/ContextTypes/featureFlag';
import { useBedrockRegistration } from 'store/bedrockRegistration';
import { useFormContext } from 'react-hook-form';
import Reaptcha from 'reaptcha';
import { useVertical } from 'lib/Hooks/useVertical';
import { pickSiteKey } from './verticalSitekeys';

/**
 * RecaptchaForm component wraps a form with optional Google reCAPTCHA verification.
 *
 * @param {object} props - The component props.
 * @param {Function} props.submitCallback - Callback to execute on form submission.
 * @param {import('lib/types/store/bedrockRegistration').TrackingPayload} [props.trackingPayload] - Optional tracking payload for analytics.
 * @param {React.ReactNode} props.children - Child components to render inside the form.
 * @returns {JSX.Element} The rendered form component.
 */
const RecaptchaForm = ({
  submitCallback, trackingPayload, children,
}) => {
  const [captcha, setCaptcha] = useState(/** @type {Reaptcha | null} */ (null));
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const setError = useBedrockRegistration((state) => state.setError);
  const track = useBedrockRegistration((state) => state.track);
  const { vertical } = useVertical();
  const siteKey = pickSiteKey(vertical);

  const { handleSubmit, getValues } = useFormContext();

  const launchDarklyFlags = useFeatureFlagContext();
  const showRecaptcha = launchDarklyFlags?.['account-login-recaptcha'];

  /**
   * Handles loading of the Reaptcha component.
   */
  const onLoad = () => {
    setCaptchaLoaded(true);
  };

  /**
   * Handles form submission, triggers tracking and recaptcha if enabled.
   * @param {object} data - The form data to submit.
   */
  const onSubmit = (data) => {
    if (trackingPayload) track(trackingPayload);

    if (!showRecaptcha) {
      // if recaptcha is not enabled, we directly submit the form
      submitCallback(data);
    } else if ((captcha && captchaLoaded)) {
      captcha.execute();
    } else {
      // If recaptcha is not loaded or not set, we show an error
      setError('There was an error submitting your form. Please try again.');
    }
  };

  /**
   * Callback for submitting the form after reCAPTCHA verification.
   */
  const submitFormCallback = () => {
    const data = getValues();
    submitCallback(data);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
    >
      {children}
      {showRecaptcha ? (
        <Reaptcha
          ref={(instance) => setCaptcha(instance)}
          sitekey={siteKey}
          onVerify={submitFormCallback}
          onLoad={onLoad}
          size="invisible"
        />
      ) : null}
    </form>
  );
};

export { RecaptchaForm };
